/**
 * Shared community-card data helpers (DEV-1447).
 *
 * Lifted verbatim out of CommunitiesPage.tsx when the browse card was
 * extracted into the shared <CommunityCard>. Both CommunitiesPage and the
 * Discover Communities catalog derive a card's flag / ancestry / hero from
 * these, so the two surfaces cannot drift.
 *
 * NOTE on the "reuse the My Impact rail's community card" binding: that
 * binding named a component that never existed — the rail's row is inline JSX
 * (CommunitySwitcherRail.tsx), and it is a 184px list ROW with no hero, no
 * flag, no ancestry and no Follow CTA. A row is not a card. Per Skip's ruling
 * (2026-07-12), the CommunitiesPage BROWSE card was extracted instead and the
 * rail keeps its row untouched. See STATUS_DESIGN_2026-07-12_DISCOVER_CATALOGS_PHASE1.md §7.3.
 */

import { communityConfigStore } from '../../Data/store';
import { getCommunityOverride } from '../../Data/CommunityConfig/communityConfigOverrides';

export type CommunityEntity = {
  entity_guid: string;
  name?: string;
  entity_type?: string;
  is_active?: boolean;
  visibility?: string;
  code?: string;
  parent_entity_guid?: string | null;
  hero_image_url?: string;
  created_at?: string;
  /**
   * DEV-961 / ADR-006. Workshop back-link. Workshops are excluded from
   * user-facing Communities lists per the agreed-upon design.
   */
  personal_owner_user_guid?: string | null;
};

export type CommunityRelationship = {
  parent_entity_guid: string;
  child_entity_guid: string;
  relationship_type?: string;
};

/** Convert an ISO 3166-1 alpha-2 country code to its flag emoji. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || code.length < 2) return '';
  const upper = code.toUpperCase().trim();
  const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    ...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

/** entity_guid (upper) → entity. */
export function buildEntityMap(entities: CommunityEntity[] | undefined): Map<string, CommunityEntity> {
  const m = new Map<string, CommunityEntity>();
  for (const e of entities || []) {
    if (!e?.entity_guid) continue;
    m.set(e.entity_guid.toUpperCase(), e);
  }
  return m;
}

/** child_guid (upper) → parent_guid (upper). */
export function buildParentMap(relationships: CommunityRelationship[] | undefined): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of relationships || []) {
    if (!r?.child_entity_guid || !r?.parent_entity_guid) continue;
    m.set(r.child_entity_guid.toUpperCase(), r.parent_entity_guid.toUpperCase());
  }
  return m;
}

/** Walk the entity hierarchy to find the country code, then return its flag. */
export function findCountryFlag(
  entity: CommunityEntity,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): string {
  const code = entity.code;
  const etype = (entity.entity_type || '').toLowerCase();
  if (etype === 'geo_country' && code) return countryFlag(code);
  let guid = parentMap.get(entity.entity_guid.toUpperCase());
  for (let i = 0; i < 5 && guid; i++) {
    const parent = entityMap.get(guid);
    if (!parent) break;
    const ptype = (parent.entity_type || '').toLowerCase();
    if (ptype === 'geo_country' && parent.code) return countryFlag(parent.code);
    guid = parentMap.get(guid);
  }
  return '';
}

/**
 * Resolve ancestor path names for a community entity.
 * Returns e.g. "Massachusetts, United States" for Boston.
 * Excludes "Earth" and the entity's own name.
 */
export function resolveAncestryLine(
  entity: CommunityEntity,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
): string {
  const names: string[] = [];
  let guid = parentMap.get(entity.entity_guid.toUpperCase());
  const seen = new Set<string>();
  for (let i = 0; i < 6 && guid && !seen.has(guid); i++) {
    seen.add(guid);
    const ancestor = entityMap.get(guid);
    if (!ancestor) break;
    const name = ancestor.name || '';
    if (name.toLowerCase() !== 'earth' && name.trim()) {
      names.push(name);
    }
    guid = parentMap.get(guid);
  }
  return names.join(', ');
}

/** Resolve hero image URL: override → API store → entity prop. */
export function resolveHeroUrl(guid: string): string | null {
  try {
    const ov = getCommunityOverride(guid) as any;
    if (ov?.hero_image_url) return ov.hero_image_url;
  } catch { /* ignore */ }
  const storeEntry = communityConfigStore.get(String(guid).toUpperCase());
  if (storeEntry?.hero_image_url) return storeEntry.hero_image_url;
  return null;
}

/**
 * Collect the full ancestor chain (self excluded), root-nearest last.
 * Used by the catalog's "Near you" shelf.
 */
export function ancestorGuids(
  guid: string,
  parentMap: Map<string, string>,
  maxHops = 8,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = parentMap.get(guid.toUpperCase());
  for (let i = 0; i < maxHops && cur && !seen.has(cur); i++) {
    seen.add(cur);
    out.push(cur);
    cur = parentMap.get(cur);
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Hero image — GUARANTEED, never gated (DEV-1447, Skip 2026-07-13)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The card wall of real place photography IS the Communities catalog's
 * signature element (the CLAUDE.md 1-second test — cover the text and you still
 * know you're looking at places). But it only works if EVERY card has an image:
 * one gray box and the signature degrades.
 *
 * The rejected alternative was to require a hero photo in order to be LISTED.
 * That was declined deliberately, and the reasoning belongs next to the code:
 *
 *   • It makes the catalog LIE. The grid is the complete index of browsable
 *     communities. Hiding Hampton because nobody uploaded a JPEG does not make
 *     Hampton less real, joinable, or worth finding.
 *   • It starves the feature's whole purpose — the catalog exists to feed the
 *     follow-3 rung basic, and gating shrinks the pool of followable communities.
 *   • It is backwards on the merits: the community that most NEEDS discovery is
 *     the new one, which is exactly the one with no photo yet.
 *   • It inverts the CLAUDE.md principle "architecture serves the product
 *     principle, not the reverse" — this would be the product bending to the skin.
 *   • Second-order: if a photo is the price of admission, admins paste in any
 *     stock image. Gray boxes become WRONG pictures, which is worse — a
 *     misleading photo of a place is a lie; a missing one is merely a gap.
 *
 * So: guarantee the hero, never gate on it. Three tiers, in order.
 */

export interface HeroResolution {
  /** Raw URL (pre-`mediaSrc`). Null → the caller renders the designed fallback. */
  url: string | null;
  /**
   * Name of the ancestor the image was borrowed from, when tier 2 fired.
   * The card surfaces this — an inherited photo is still a picture of where you
   * are, just at a coarser grain, and saying so keeps it honest rather than
   * passing off a photo of Rhode Island as a photo of Pawtucket.
   */
  inheritedFrom: string | null;
}

/** The community's own hero, from any of the three stores. */
function ownHero(entity: CommunityEntity): string | null {
  return resolveHeroUrl(entity.entity_guid) || entity.hero_image_url || null;
}

/**
 * Tier 1 — the community's own photo.
 * Tier 2 — the nearest ancestor's photo (Pawtucket → Rhode Island → USA).
 * Tier 3 — null, and the card draws a designed place-treatment. Never a gray box.
 */
export function resolveCardHero(
  entity: CommunityEntity,
  entityMap: Map<string, CommunityEntity>,
  parentMap: Map<string, string>,
  maxHops = 8,
): HeroResolution {
  const own = ownHero(entity);
  if (own) return { url: own, inheritedFrom: null };

  for (const guid of ancestorGuids(entity.entity_guid, parentMap, maxHops)) {
    const ancestor = entityMap.get(guid);
    if (!ancestor) continue;
    const hero = ownHero(ancestor);
    if (hero) {
      return { url: hero, inheritedFrom: ancestor.name || null };
    }
  }

  return { url: null, inheritedFrom: null };
}

/**
 * Tier 3 — a deterministic place-treatment for a community with no photo
 * anywhere up its chain (in practice: a root, or a fresh dataset).
 *
 * Deterministic from the guid, so a given community always looks the same —
 * a card that reshuffles its colours between renders reads as broken. Two hues
 * far enough apart to feel like a landscape rather than a flat swatch.
 */
export function placeholderHeroGradient(guid: string): string {
  let hash = 0;
  const g = String(guid || '').toUpperCase();
  for (let i = 0; i < g.length; i++) {
    hash = (hash * 31 + g.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  const hue2 = (hue + 42) % 360;
  return (
    `linear-gradient(150deg, ` +
    `hsl(${hue} 42% 30%) 0%, ` +
    `hsl(${hue2} 38% 20%) 55%, ` +
    `hsl(${hue2} 30% 13%) 100%)`
  );
}
